import type { LatLon } from '../geo';
import { distanceNm, initialBearingDeg, destinationPoint, interpolate } from '../geo';
import type { Route } from '../map/navigation';
import { directRoute } from '../map/navigation';
import type { VesselProfile, SpeedSetting } from './profiles';
import type { WeatherField } from './weather';
import { currentAlongCourse } from './weather';

/**
 * Die Fahrt zum Einsatzort.
 *
 * Kein Arcade-Steuern: Der Spieler waehlt eine Fahrtstufe und lebt mit den
 * Folgen. Schneller ankommen heisst mehr Treibstoff verbrennen und bei schwerer
 * See den Rumpf strapazieren — und der Schaden begleitet ihn in die naechste
 * Mission.
 *
 * `estimate()` rechnet dieselbe Simulation vorab durch. Ohne diese Vorschau
 * waere die Entscheidung ein Ratespiel statt einer Abwaegung.
 */

export interface VoyageConditions {
  readonly seaState: number;
  readonly effectiveKnots: number;
  readonly currentAssistKnots: number;
}

export interface VoyageState {
  readonly position: LatLon;
  /** Kampagnenzeit in Stunden. */
  readonly clockHours: number;
  readonly fuel: number;
  /** Rumpfzustand in Prozent. */
  readonly hull: number;
  readonly speed: SpeedSetting;
  readonly remainingNm: number;
  readonly arrived: boolean;
  /**
   * Das Fahrzeug liegt fest, bevor es angekommen ist — ohne Treibstoff oder
   * mit zerschlagenem Rumpf. Die Fahrt ist damit gescheitert.
   */
  readonly stranded: boolean;
  /** Warum es festliegt; `null`, solange es faehrt. */
  readonly disabledBy: 'fuel' | 'hull' | null;
}

/**
 * Der veraenderliche Teil einer Fahrt — alles, was sich nicht nachrechnen
 * laesst und deshalb in den Spielstand gehoert.
 */
export interface VoyageSnapshot {
  readonly position: LatLon;
  /** Wegpunkt, auf den gerade zugehalten wird. */
  readonly legIndex: number;
  readonly clockHours: number;
  readonly fuel: number;
  readonly hull: number;
  readonly speed: SpeedSetting;
}

export interface VoyageEstimate {
  readonly hours: number;
  readonly fuelUsed: number;
  readonly hullLoss: number;
  readonly worstSeaState: number;
  /** Reicht der Treibstoff fuer Hin- und Rueckweg? */
  readonly fuelSufficient: boolean;
}

/** Wie stark der Seegang die Fahrt bremst. */
function seaSpeedFactor(seaState: number, exposed: boolean): number {
  if (!exposed) return 1;
  // Bei Seegang 9 bleiben rund 55 Prozent der Fahrt uebrig.
  return Math.max(0.5, 1 - seaState * 0.05);
}

/** Zusaetzlicher Verbrauch, wenn die See gegen den Rumpf arbeitet. */
function seaFuelFactor(seaState: number, exposed: boolean): number {
  if (!exposed) return 1;
  return 1 + seaState * 0.06;
}

/** Rumpfbelastung je Stunde. */
function hullStressPerHour(
  seaState: number,
  setting: SpeedSetting,
  profile: VesselProfile,
  factor = 1,
): number {
  if (!profile.weatherExposed) return 0;
  // Unterhalb Seegang 4 passiert nichts — erst darueber wird es ruppig.
  const excess = Math.max(0, seaState - 4);
  if (excess === 0) return 0;

  const bySetting: Record<SpeedSetting, number> = { slow: 0.25, cruise: 0.7, full: 1.6 };
  return excess * bySetting[setting] * profile.hullFragility * 0.5 * factor;
}

export class Voyage {
  readonly profile: VesselProfile;
  readonly origin: LatLon;
  readonly destination: LatLon;
  /**
   * Der gefahrene Kurs als Folge von Wegpunkten.
   *
   * Ohne ihn fuehre das Schiff die Luftlinie — und die kreuzt im Dreieck
   * regelmaessig die flachen Baenke der Bahamas.
   */
  readonly route: Route;

  private readonly weather: WeatherField;
  private readonly startHours: number;

  private position: LatLon;
  /** Wegpunkt, auf den gerade zugehalten wird. */
  private legIndex = 1;
  private clock: number;
  private fuelLeft: number;
  private hullLeft: number;
  private setting: SpeedSetting;
  /**
   * Faktor auf die Rumpfbelastung — kommt vom Hintergrund der Figur.
   * Der Marineoffizier faehrt schwere See schonender ab.
   */
  private readonly hullStressFactor: number;
  /** Faktor auf den Treibstoffverbrauch — kommt vom Schwierigkeitsgrad. */
  private readonly fuelFactor: number;

  constructor(params: {
    profile: VesselProfile;
    from: LatLon;
    to: LatLon;
    weather: WeatherField;
    startHours: number;
    fuel?: number;
    hull?: number;
    speed?: SpeedSetting;
    route?: Route;
    hullStressFactor?: number;
    fuelFactor?: number;
  }) {
    this.profile = params.profile;
    this.origin = params.from;
    this.destination = params.to;
    this.route = params.route ?? directRoute(params.from, params.to);
    this.weather = params.weather;
    this.startHours = params.startHours;

    this.position = params.from;
    this.clock = params.startHours;
    this.fuelLeft = params.fuel ?? params.profile.fuelCapacity;
    this.hullLeft = params.hull ?? 100;
    this.setting = params.speed ?? 'cruise';
    this.hullStressFactor = params.hullStressFactor ?? 1;
    this.fuelFactor = params.fuelFactor ?? 1;
  }

  /** Noch anzulaufende Wegpunkte — Grundlage der Kursdarstellung. */
  get remainingWaypoints(): readonly LatLon[] {
    return this.route.waypoints.slice(this.legIndex);
  }

  /** Der Wegpunkt, auf den gerade zugehalten wird. */
  private get activeWaypoint(): LatLon {
    return (this.route.waypoints[this.legIndex] ?? this.destination) as LatLon;
  }

  /** Reststrecke entlang des Kurses, nicht Luftlinie zum Ziel. */
  private get remainingAlongRoute(): number {
    let total = distanceNm(this.position, this.activeWaypoint);
    for (let i = this.legIndex + 1; i < this.route.waypoints.length; i++) {
      total += distanceNm(this.route.waypoints[i - 1] as LatLon, this.route.waypoints[i] as LatLon);
    }
    return total;
  }

  get state(): VoyageState {
    const remaining = this.remainingAlongRoute;
    /*
     * Ein Rumpf bei null faehrt nicht weiter.
     *
     * Frueher war das nur eine Zahl: Das Schiff lief mit 0 % Rumpf weiter und
     * kam an, als waere nichts gewesen. Damit war die Rumpfbelastung — die
     * Kehrseite der vollen Fahrt bei schwerer See — folgenlos.
     */
    const disabledBy =
      remaining < 0.5 ? null : this.fuelLeft <= 0 ? 'fuel' : this.hullLeft <= 0 ? 'hull' : null;
    return {
      position: this.position,
      clockHours: this.clock,
      fuel: this.fuelLeft,
      hull: this.hullLeft,
      speed: this.setting,
      remainingNm: remaining,
      arrived: distanceNm(this.position, this.destination) < 0.5,
      stranded: disabledBy !== null,
      disabledBy,
    };
  }

  get elapsedHours(): number {
    return this.clock - this.startHours;
  }

  /**
   * Der veraenderliche Teil der Fahrt, als schlichte Daten.
   *
   * Fuer den Spielstand. Alles Uebrige — Kurs, Profil, Wetter, Start- und
   * Zielpunkt — laesst sich aus dem Seed und dem Auftrag wieder herstellen und
   * gehoert deshalb nicht hier hinein. Was sich *nicht* nachrechnen laesst,
   * ist der Fortschritt: wie weit gefahren, wie viel verbraucht, auf welchen
   * Wegpunkt gerade zugehalten wird.
   */
  snapshot(): VoyageSnapshot {
    return {
      position: { ...this.position },
      legIndex: this.legIndex,
      clockHours: this.clock,
      fuel: this.fuelLeft,
      hull: this.hullLeft,
      speed: this.setting,
    };
  }

  /** Setzt einen Abzug wieder ein. Gegenstueck zu `snapshot`. */
  applySnapshot(snapshot: VoyageSnapshot): void {
    this.position = { ...snapshot.position };
    // Gegen einen Kurs, der sich zwischen zwei Fassungen geaendert hat: Ein
    // Wegpunktindex ausserhalb der Route liesse das Schiff ins Leere steuern.
    this.legIndex = Math.min(Math.max(snapshot.legIndex, 0), this.route.waypoints.length - 1);
    this.clock = snapshot.clockHours;
    this.fuelLeft = snapshot.fuel;
    this.hullLeft = snapshot.hull;
    this.setting = snapshot.speed;
  }

  setSpeed(setting: SpeedSetting): void {
    this.setting = setting;
  }

  /**
   * Laesst Zeit vergehen und Vorraete schwinden, ohne dass sich das Fahrzeug
   * bewegt — etwa waehrend eines Abstechers zu einem Sonarkontakt.
   *
   * Genau hier entsteht der Preis einer Untersuchung unterwegs: Die Uhr des
   * Vorfalls laeuft weiter, die Reststrecke bleibt.
   */
  spendTime(hours: number, fuel = 0, hullDamage = 0): void {
    if (hours > 0) this.clock += hours;

    // Ein negativer Betrag ist Nachschub, kein Verbrauch: Seit M8 kann
    // unterwegs von einem Versorger gebunkert werden. Mehr als in die Tanks
    // passt, geht dabei nicht hinein — sonst waere der Bunkerhalt eine
    // Erhoehung der Reichweite statt einer Auffuellung.
    if (fuel !== 0) {
      this.fuelLeft = Math.max(0, Math.min(this.profile.fuelCapacity, this.fuelLeft - fuel));
    }

    if (hullDamage > 0) this.hullLeft = Math.max(0, this.hullLeft - hullDamage);
  }

  /** Aktuelle Bedingungen am Ort des Fahrzeugs. */
  conditions(): VoyageConditions {
    return this.conditionsAt(this.position, this.clock, this.setting);
  }

  private conditionsAt(
    position: LatLon,
    hours: number,
    setting: SpeedSetting,
  ): VoyageConditions {
    const seaState = this.weather.seaStateAt(position, hours);
    // Die Stroemung wirkt auf den anliegenden Kurs, nicht auf die Luftlinie —
    // auf einem Umweg um eine Bank kann derselbe Strom bremsen statt schieben.
    const course = initialBearingDeg(position, this.activeWaypoint);
    const current = this.weather.currentAt(position, hours);

    const assist = this.profile.weatherExposed
      ? currentAlongCourse(course, current.setDeg) * current.driftKnots
      : // Getaucht wirkt die Oberflaechenstroemung nur gedaempft.
        currentAlongCourse(course, current.setDeg) * current.driftKnots * 0.4;

    const through = this.profile.knots[setting] * seaSpeedFactor(seaState, this.profile.weatherExposed);

    return {
      seaState,
      effectiveKnots: Math.max(1, through + assist),
      currentAssistKnots: assist,
    };
  }

  /**
   * Bewegt die Fahrt um die angegebene Zeit weiter.
   * Liefert die zurueckgelegte Strecke in Seemeilen.
   */
  advance(hours: number): number {
    if (hours <= 0) return 0;

    const state = this.state;
    if (state.arrived || state.stranded) return 0;

    const conditions = this.conditions();
    const possible = conditions.effectiveKnots * hours;
    // Nur bis zum naechsten Wegpunkt rechnen; der Rest folgt im naechsten Schritt.
    const toWaypoint = distanceNm(this.position, this.activeWaypoint);
    const travelled = Math.min(possible, toWaypoint);
    // Bei vorzeitiger Ankunft nur den tatsaechlich benoetigten Anteil abrechnen.
    const usedHours = conditions.effectiveKnots > 0 ? travelled / conditions.effectiveKnots : hours;

    const burn =
      this.profile.fuelPerHour[this.setting] *
      seaFuelFactor(conditions.seaState, this.profile.weatherExposed) *
      this.fuelFactor *
      usedHours;

    // Der Treibstoff reicht moeglicherweise nicht fuer den ganzen Schritt.
    const affordable = burn > 0 ? Math.min(1, this.fuelLeft / burn) : 1;
    const actualHours = usedHours * affordable;
    const actualTravelled = travelled * affordable;

    if (actualTravelled > 0) {
      const course = initialBearingDeg(this.position, this.activeWaypoint);
      if (actualTravelled >= toWaypoint - 1e-9) {
        // Wegpunkt erreicht — auf den naechsten Schenkel weiterschalten.
        this.position = this.activeWaypoint;
        if (this.legIndex < this.route.waypoints.length - 1) this.legIndex++;
      } else {
        this.position = destinationPoint(this.position, course, actualTravelled);
      }
    }

    this.clock += actualHours;
    this.fuelLeft = Math.max(0, this.fuelLeft - burn * affordable);
    this.hullLeft = Math.max(
      0,
      this.hullLeft -
        hullStressPerHour(conditions.seaState, this.setting, this.profile, this.hullStressFactor) *
          actualHours,
    );

    return actualTravelled;
  }

  /**
   * Rechnet die Fahrt bei gleichbleibender Fahrtstufe voraus, ohne den Zustand
   * zu veraendern. Grundlage der Anzeige, auf der der Spieler entscheidet.
   */
  estimate(setting: SpeedSetting = this.setting, samples = 24): VoyageEstimate {
    const total = this.remainingAlongRoute;
    if (total < 0.5) {
      return { hours: 0, fuelUsed: 0, hullLoss: 0, worstSeaState: 0, fuelSufficient: true };
    }

    let hours = 0;
    let fuel = 0;
    let hull = 0;
    let worstSea = 0;

    // Die Route in Abschnitte teilen und je Abschnitt mit den dort und dann
    // herrschenden Bedingungen rechnen.
    const step = total / samples;
    for (let i = 0; i < samples; i++) {
      const at = this.pointAlongRoute((i + 0.5) / samples);
      const conditions = this.conditionsAt(at, this.clock + hours, setting);

      const segmentHours = step / conditions.effectiveKnots;
      hours += segmentHours;
      fuel +=
        this.profile.fuelPerHour[setting] *
        seaFuelFactor(conditions.seaState, this.profile.weatherExposed) *
        this.fuelFactor *
        segmentHours;
      hull +=
        hullStressPerHour(conditions.seaState, setting, this.profile, this.hullStressFactor) *
        segmentHours;
      worstSea = Math.max(worstSea, conditions.seaState);
    }

    return {
      hours,
      fuelUsed: fuel,
      hullLoss: hull,
      worstSeaState: worstSea,
      // Die Rueckfahrt muss mit hinein, sonst strandet man am Fundort.
      fuelSufficient: fuel * 2 <= this.fuelLeft,
    };
  }

  /**
   * Punkt auf dem verbleibenden Kurs, `fraction` von 0 bis 1.
   * Wird fuer die Vorausberechnung gebraucht: Sie muss das Wetter dort
   * abtasten, wo das Schiff tatsaechlich entlangfaehrt.
   */
  private pointAlongRoute(fraction: number): LatLon {
    const target = this.remainingAlongRoute * fraction;
    let travelled = 0;
    let previous = this.position;

    for (let i = this.legIndex; i < this.route.waypoints.length; i++) {
      const next = this.route.waypoints[i] as LatLon;
      const legLength = distanceNm(previous, next);

      if (travelled + legLength >= target) {
        const withinLeg = legLength > 0 ? (target - travelled) / legLength : 0;
        return interpolate(previous, next, withinLeg);
      }

      travelled += legLength;
      previous = next;
    }

    return this.destination;
  }

  /** Vergleicht alle Fahrtstufen — die eigentliche Entscheidungsgrundlage. */
  compareSettings(): Record<SpeedSetting, VoyageEstimate> {
    return {
      slow: this.estimate('slow'),
      cruise: this.estimate('cruise'),
      full: this.estimate('full'),
    };
  }
}
